import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Moon, Sun } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SiteProvider, useSite, useSiteLang } from "@/hooks/useSite";
import LanguagePicker from "@/components/LanguagePicker";
import SiteFooter from "@/components/site/SiteFooter";
import { JoinWaitlistButton, Wordmark } from "@/components/site/SiteBits";
import "@/site.css";

export const SITE_NAV = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/plans", label: "Plans" },
  { to: "/safety", label: "Safety" },
  { to: "/faq", label: "FAQ" },
];
const THEME_KEY = "cw_site_theme";

// Page views: one POST per route change. Scroll: top on each navigation, or to #hash once rendered (focus its first field).
function usePageViews() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => { api("/public/pageview", { auth: false, body: { path: pathname } }).catch(() => {}); }, [pathname]);
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (!el && ++tries < 60) return;
      clearInterval(t);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.querySelector("input:not([tabindex='-1'])")?.focus({ preventScroll: true });
    }, 50);
    return () => clearInterval(t);
  }, [pathname, hash, key]);
}

// Theme: saved choice, else the OS setting (index.html already applied it before paint). Removed when leaving the website.
function useSiteTheme() {
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-site-theme")
    || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"));
  useEffect(() => {
    document.documentElement.setAttribute("data-site-theme", theme);
    return () => document.documentElement.removeAttribute("data-site-theme");
  }, [theme]);
  const toggle = () => setTheme((t) => { const n = t === "dark" ? "light" : "dark"; try { localStorage.setItem(THEME_KEY, n); } catch { /* private mode */ } return n; });
  return [theme, toggle];
}

function Header({ theme, onToggle }) {
  const cta = useSite().data?.docs?.home_copy?.fields?.cta;
  const { lang, languages, setLang } = useSiteLang();
  const navCls = ({ isActive }) => `s-focus rounded-full px-3.5 py-2 text-[0.9375rem] transition-colors ${isActive ? "font-bold" : "s-muted font-medium hover:text-[var(--s-text)]"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-transparent bg-[var(--s-bg)]/80 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-3 px-5 sm:px-8">
        <Link to="/" aria-label="Crushwink home" className="s-focus rounded-lg" data-testid="site-header-logo-link"><Wordmark testId="site-header-logo" /></Link>
        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {SITE_NAV.map((n) => <NavLink key={n.to} to={n.to} className={navCls} data-testid={`site-nav-${n.to.slice(1)}`}>{n.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguagePicker value={lang} languages={languages} onChange={setLang} testId="site-header-language" />
          <button type="button" onClick={onToggle} className="s-round s-focus" aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"} data-testid="site-theme-toggle">
            {theme === "dark" ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
          </button>
          <JoinWaitlistButton label={cta} className="max-w-[8rem] !h-auto min-h-[2.75rem] px-4 py-1.5 text-center !text-[0.8125rem] leading-tight !whitespace-normal sm:max-w-none sm:px-5 sm:!text-[0.9375rem] sm:!whitespace-nowrap" testId="site-header-waitlist-button" />
        </div>
      </div>
    </header>
  );
}

export default function SiteLayout() {
  usePageViews();
  const [theme, toggle] = useSiteTheme();
  return (
    <SiteProvider>
      <div className="site-root flex min-h-screen flex-col" data-testid="site-layout" data-theme={theme}>
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-black">Skip to content</a>
        <Header theme={theme} onToggle={toggle} />
        <main id="main" className="flex-1"><Outlet /></main>
        <SiteFooter />
      </div>
    </SiteProvider>
  );
}
